import {ComponentType} from "react";
import {
  useParams,
  useLocation,
  useNavigate,
  type Location,
  type To,
} from "react-router-dom";

export type RouterInjectedProps = {
  match: {
    params: ReturnType<typeof useParams>;
    path: string;
    url: string;
  };
  location: Location;
  history: {
    push: (to: To, state?: unknown) => void;
    replace: (to: To) => void;
    goBack: () => void;
  };
};

/** v5-shaped route props for components wrapped with {@link withRouterProps}. */
export type RouteComponentProps<
  Params extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
> = RouterInjectedProps & {
  match: RouterInjectedProps["match"] & {params: Readonly<Params>};
};

export function withRouterProps<P extends object>(
  Component: ComponentType<P & RouterInjectedProps>,
): ComponentType<P> {
  return function WithRouterProps(props: P) {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const history = {
      push: (to: To, state?: unknown) => {
        if (typeof to === "string") navigate(to, {state});
        else navigate(to, {state});
      },
      replace: (to: To) => navigate(to, {replace: true}),
      goBack: () => navigate(-1),
    };
    const match = {params, path: "", url: location.pathname};
    return (
      <Component
        {...props}
        match={match}
        location={location}
        history={history}
      />
    );
  };
}
